import { el, clear, toast } from '../core/dom.js';
import { getState, planFor } from '../core/state.js';
import { consumptionMethods, episodes, events as eventsStore, habits, metaSet, plans, planStates, purchaseTemplates } from '../storage/db.js';
import { nowIso } from '../core/time.js';
import { CATEGORY_TO_DOMAIN, NICOTINE_DEFAULT_METHODS, buildMethodFromSeed } from '../domain/default-methods.js';
import { uuid } from '../core/ids.js';
import { navigate } from '../router.js';
import { habitDisplayLabel } from '../core/display.js';
import type { HabitRecord, IntervalConfig, IntervalState, PlanRecord, PlanStateRecord } from '../core/types.js';

/** Tag-styled button with an in-flight guard so rapid taps don't re-fire IDB writes. */
function guardedTag(label: string, run: () => Promise<void>, variant?: 'danger'): HTMLElement {
  const style = variant === 'danger' ? 'border-color:var(--danger); color:var(--danger);' : '';
  return el('button', {
    class: 'tag',
    attrs: { type: 'button', ...(style ? { style } : {}) },
    onclick: async (e: Event) => {
      const btn = e.currentTarget as HTMLButtonElement;
      if (btn.disabled) return;
      btn.disabled = true;
      try {
        await run();
      } catch (err) {
        console.error(`${label} failed:`, err);
        toast('Could not save — try again.');
      } finally {
        btn.disabled = false;
      }
    },
  }, label);
}

export async function renderPlans(parent: HTMLElement) {
  clear(parent);
  const st = getState();
  const allHabits = [...st.habits].sort((a, b) => Number(b.active) - Number(a.active) || a.createdAt.localeCompare(b.createdAt));

  parent.appendChild(el('div', { class: 'card' },
    el('h2', {}, 'Manage plans'),
    el('p', { class: 'muted' }, `${allHabits.filter((h) => h.active).length} active · ${allHabits.filter((h) => !h.active).length} archived`),
    el('button', { class: 'btn primary block', onclick: () => navigate('/onboard') }, '+ Create new plan'),
  ));

  const mergeCard = nicotineMergeOffer(allHabits);
  if (mergeCard) parent.appendChild(mergeCard);

  for (const h of allHabits) parent.appendChild(habitCard(h));
}

function habitCard(h: HabitRecord): HTMLElement {
  const st = getState();
  const isDefault = st.meta.active_habit_id === h.id;
  const { plan, planState } = planFor(h.id, st);

  const status: string[] = [];
  if (h.active) status.push('active'); else status.push('archived');
  if (isDefault) status.push('default');
  if (plan) status.push(plan.planType);

  const card = el('div', { class: 'card', attrs: { style: h.active ? '' : 'opacity:0.7;' } },
    el('div', { attrs: { style: 'display:flex; align-items:center; justify-content:space-between; gap:0.5rem; flex-wrap:wrap;' } },
      el('div', {},
        el('div', { attrs: { style: 'font-weight:700; font-size:1.05rem;' } }, habitDisplayLabel(h)),
        el('div', { class: 'muted', attrs: { style: 'font-size:0.8rem;' } }, status.join(' · ')),
      ),
      el('div', { class: 'kbd-list' },
        h.active && plan
          ? el('a', { class: 'tag', href: `#/plan?habit=${h.id}` }, 'Edit')
          : null,
        h.active && plan
          ? el('a', { class: 'tag', href: `#/progress?habit=${h.id}` }, 'Progress')
          : null,
        !isDefault && h.active
          ? guardedTag('Make default', () => makeDefault(h))
          : null,
        h.active
          ? guardedTag('Archive', () => archive(h))
          : guardedTag('Unarchive', () => unarchive(h)),
        !h.active
          ? guardedTag('Delete', () => deleteForever(h), 'danger')
          : null,
      ),
    ),
    planState
      ? el('div', { class: 'muted', attrs: { style: 'margin-top:0.5rem; font-size:0.85rem;' } }, planState.state.lastExplanation ?? '—')
      : null,
  );
  return card;
}

async function makeDefault(h: HabitRecord) {
  const st = getState();
  const { plan } = planFor(h.id, st);
  await metaSet('active_habit_id', h.id);
  if (plan) await metaSet('active_plan_id', plan.id);
  toast(`"${h.privateLabel ?? h.label}" set as default.`);
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}

async function archive(h: HabitRecord) {
  const st = getState();
  const activeCount = st.habits.filter((x) => x.active).length;
  if (activeCount <= 1) { toast('Add another plan first — at least one must stay active.'); return; }
  const label = st.settings.privacyMode ? 'this plan' : `"${h.privateLabel ?? h.label}"`;
  if (!confirm(`Archive ${label}? Logging stops; history is preserved.`)) return;
  await habits.put({ ...h, active: false, archivedAt: nowIso(), updatedAt: nowIso() });
  if (st.meta.active_habit_id === h.id) {
    const next = st.habits.find((x) => x.active && x.id !== h.id);
    if (next) {
      await metaSet('active_habit_id', next.id);
      const { plan } = planFor(next.id, st);
      if (plan) await metaSet('active_plan_id', plan.id);
    }
  }
  toast('Archived.');
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}

async function unarchive(h: HabitRecord) {
  await habits.put({ ...h, active: true, archivedAt: undefined, updatedAt: nowIso() });
  toast('Restored.');
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}

function nicotineMergeOffer(allHabits: HabitRecord[]): HTMLElement | null {
  const nicotineActive = allHabits.filter((h) => h.active && (h.domain ?? CATEGORY_TO_DOMAIN[h.category]) === 'nicotine');
  if (nicotineActive.length < 2) return null;
  // Already merged once (label "Nicotine" with multi-method) — don't pester.
  const alreadyMerged = nicotineActive.find((h) => h.label === 'Nicotine');
  if (alreadyMerged) return null;
  const labels = nicotineActive.map((h) => h.privateLabel ?? h.label).join(', ');
  return el('div', { class: 'card', attrs: { style: 'border-color: var(--accent);' } },
    el('h2', {}, 'Combine into one Nicotine plan?'),
    el('p', { class: 'muted' }, `You have ${nicotineActive.length} nicotine-related plans (${labels}). Wean can combine them into one plan with separate log buttons. History is preserved.`),
    el('div', { class: 'row' },
      el('button', { class: 'btn primary', onclick: () => mergeNicotine(nicotineActive) }, 'Combine'),
      el('button', { class: 'btn ghost', onclick: () => toast('Skipped. You can do this later from this screen.') }, 'Not now'),
    ),
  );
}

async function mergeNicotine(legacy: HabitRecord[]): Promise<void> {
  if (!confirm('Combine these plans into one Nicotine plan? Old plans become archived; events are preserved.')) return;
  const st = getState();
  const now = nowIso();

  // Build new habit
  const newId = uuid();
  const newHabit: HabitRecord = {
    id: newId, schemaVersion: 2,
    label: 'Nicotine', category: 'smoking', domain: 'nicotine',
    unitSingular: 'nicotine session', unitPlural: 'nicotine sessions',
    active: true, createdAt: now, updatedAt: now,
  };
  await habits.put(newHabit);

  // Methods derived from legacy categories
  const seenKinds = new Set<string>();
  for (const h of legacy) {
    const kind = h.category === 'vaping' ? 'vape' : 'cigarette';
    if (seenKinds.has(kind)) continue;
    const seed = NICOTINE_DEFAULT_METHODS.find((s) => s.kind === kind);
    if (seed) {
      await consumptionMethods.put(buildMethodFromSeed(newHabit, 'nicotine', seed));
      seenKinds.add(kind);
    }
  }
  // Always include cigarette + vape as common defaults if missing
  for (const k of ['cigarette', 'vape']) {
    if (seenKinds.has(k)) continue;
    const seed = NICOTINE_DEFAULT_METHODS.find((s) => s.kind === k);
    if (seed) await consumptionMethods.put(buildMethodFromSeed(newHabit, 'nicotine', seed));
  }

  // Pick pace/slip from first legacy plan
  const firstHabit = legacy[0];
  if (!firstHabit) return;
  const { plan: firstPlan } = planFor(firstHabit.id, st);
  const baselineMin = 30;
  const cfg: IntervalConfig = {
    baselineIntervalMs: baselineMin * 60_000,
    startingIntervalMs: Math.round(baselineMin * 1.1) * 60_000,
    minIntervalMs: 5 * 60_000,
    maxIntervalMs: 12 * 3600_000,
  };
  const newPlanId = uuid();
  const plan: PlanRecord = {
    id: newPlanId, habitId: newId, schemaVersion: 2, planType: 'interval',
    pace: firstPlan?.pace ?? 'steady',
    slipStyle: firstPlan?.slipStyle ?? 'balanced',
    goalStyle: firstPlan?.goalStyle ?? [],
    config: cfg, active: true, createdAt: now, updatedAt: now,
  };
  await plans.put(plan);
  const state: IntervalState = {
    planType: 'interval',
    targetIntervalMs: cfg.startingIntervalMs,
    baselineIntervalMs: cfg.baselineIntervalMs,
    minIntervalMs: cfg.minIntervalMs,
    maxIntervalMs: cfg.maxIntervalMs,
    successStreak: 0, slipStreak: 0, momentum: 50,
  };
  const ps: PlanStateRecord = { planId: newPlanId, habitId: newId, schemaVersion: 2, state, updatedAt: now };
  await planStates.put(ps);

  // Archive legacy
  for (const h of legacy) {
    await habits.put({ ...h, active: false, archivedAt: now, updatedAt: now });
    const old = st.plans.filter((p) => p.habitId === h.id);
    for (const p of old) await plans.put({ ...p, active: false, updatedAt: now });
  }

  await metaSet('active_habit_id', newId);
  await metaSet('active_plan_id', newPlanId);
  toast('Nicotine plan created. Old plans archived.');
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
  navigate('/today');
}

async function deleteForever(h: HabitRecord) {
  const privacyMode = getState().settings.privacyMode;
  const label = privacyMode ? 'this plan' : `"${h.privateLabel ?? h.label}"`;
  if (!confirm(`Permanently delete ${label} AND all its events? This cannot be undone.`)) return;
  if (!confirm('Really delete? Tap OK to confirm.')) return;
  // Cascade: delete every record tied to this habit — plans, plan states,
  // events, consumption methods, purchase templates, episodes — and finally
  // the habit row. Otherwise the "Delete" promise is a lie.
  const planList = await plans.byHabit(h.id);
  for (const p of planList) {
    await planStates.delete(p.id);
    await plans.delete(p.id);
  }
  const eventList = await eventsStore.byHabit(h.id);
  for (const e of eventList) await eventsStore.delete(e.id);
  const methodList = await consumptionMethods.byHabit(h.id);
  for (const m of methodList) await consumptionMethods.delete(m.id);
  const templateList = await purchaseTemplates.byHabit(h.id);
  for (const t of templateList) await purchaseTemplates.delete(t.id);
  const episodeList = await episodes.byHabit(h.id);
  for (const ep of episodeList) await episodes.delete(ep.id);
  await habits.delete(h.id);
  toast('Deleted.');
  window.dispatchEvent(new CustomEvent('wean:state-changed'));
}
